<template>
<v-dialog
    v-model="dialog"
    max-width="920"
>
    <v-card>
    <v-card-title class="headline">Edit Contact</v-card-title>

    <v-card-text>
        <v-form v-model="valid">

        <!------------------------------------- NAME ------------------------------------------------->

        <m-contact-line 
            left_icon="mdi-account" 
            label="Prefix" 
            v-model="contact.name.prefix"
            v-show="show_more_fields"
        />

        <m-contact-line 
            :left_icon="show_more_fields ? 'mdi-blank' : 'mdi-account'" 
            label="First Name" 
            v-model="contact.name.first"
        />

        <m-contact-line 
            label="Middle Name" 
            v-model="contact.name.middle"
            v-show="show_more_fields"
        />

        <m-contact-line 
            label="Last Name" 
            v-model="contact.name.last"
        />

        <m-contact-line 
            label="Suffix" 
            v-model="contact.name.suffix"
            v-show="show_more_fields"
        />

        <m-contact-line 
            label="Phonetic" 
            v-model="contact.name.phonetic"
            v-show="show_more_fields"
        />

        <m-contact-line 
            label="Nickname" 
            v-model="contact.name.nickname"
            v-show="show_more_fields"
        />
        
        <m-contact-line 
            label="Label As" 
            hint="If filled out, the contact will show this label throughout the system"
            v-model="contact.name.explicit_label"
            v-show="show_more_fields"
        />
        
        <!------------------------------------- EMPLOYMENT SUMMARY ------------------------------------------------->

        <m-contact-line 
            left_icon="mdi-domain"
            label="Company" 
            v-model="contact.employment_summary.organization_name"
        />

        <m-contact-line 
            label="Job Title" 
            v-model="contact.employment_summary.job_title"
        />

        <m-contact-line 
            label="Department" 
            v-model="contact.employment_summary.department"
            v-show="show_more_fields"
        />

        <!-------------------------------------- PHONE NUMBERS -------------------------------------------------->

        <div
            v-for="(phone, index) in contact.phone_numbers"
            :key="index+'_phone'"
        >
            <m-contact-line 
                left_icon="mdi-phone"
                label="Phone Number" 
                v-model="phone.phone_number"
                phone_number
                :disabled="isPhoneDisabled(index,phone)"
                :show_add="isLastPhone(index)"
                show_delete
                v-on:on-delete="deletePhone(index)"
                v-on:on-add="addPhone()"
            />

            <m-contact-line 
                label="Label" 
                v-model="phone.label"
                :disabled="isPhoneDisabled(index,phone)"
            />
        </div>

        <!-------------------------------------- EMAIL ADDRESSES -------------------------------------------------->

        <div
            v-for="(email, index) in contact.email_addresses"
            :key="index+'_email'"
        >
            <m-contact-line 
                left_icon="mdi-email"
                label="Email Address" 
                v-model="email.email"
                email
                :show_add="isLastEmail(index)"
                show_delete
                v-on:on-delete="deleteEmail(index)"
                v-on:on-add="addEmail()"
            />

            <m-contact-line 
                label="Label" 
                v-model="email.label"
            />
        </div>


        <!-------------------------------------- POSTAL ADDRESSES -------------------------------------------------->

        <div
            v-for="(address, index) in contact.postal_addresses"
            :key="index+'_postal_address'"
            v-show="show_more_fields"
        >
            <m-contact-line 
                left_icon="mdi-map-marker"
                label="Address Label" 
                hint="e.g. Home, Office, Warehouse, etc."
                v-model="address.label"
                :show_add="isLastAddress(index)"
                show_delete
                v-on:on-delete="deleteAddress(index)"
                v-on:on-add="addAddress()"
            />

            <m-contact-line label="Address Line 1" v-model="address.address.address_line1"/>
            <m-contact-line label="Address Line 2" v-model="address.address.address_line2"/>
            <m-contact-line label="Address Line 3" v-model="address.address.address_line3"/>
            <m-contact-line label="City" v-model="address.address.city"/>
            <m-contact-line label="State" v-model="address.address.state"/>
            <m-contact-line label="Zip Code" v-model="address.address.postal_code"/>
            <m-contact-line label="Country Code" v-model="address.address.country"/>

        </div>

    


        <!-------------------------------------- EXTENDED FIELDS -------------------------------------------------->
        <m-contact-line 
            left_icon="mdi-cake-variant"
            label="Birthday" 
            hint="(MM/DD or MM/DD/YYYY) - e.g. 2/8 for Feb 8th, 2/8/1981 for Feb 8th 1981"
            us_date_with_optional_year
            v-model="contact.birthday"
            v-show="show_more_fields"
        />

        <!-------------------------------------- OTHER EVENTS -------------------------------------------------->

        <div
            v-for="(event, index) in contact.events"
            :key="index+'_events'"
            v-show="show_more_fields"
        >
            <m-contact-line 
                left_icon="mdi-calendar"
                label="Event Date" 
                v-model="event.date"
                us_date_with_optional_year
                :show_add="isLastEvent(index)"
                show_delete
                v-on:on-delete="deleteEvent(index)"
                v-on:on-add="addEvent()"
            />

             <m-contact-line 
                label="Label" 
                v-model="event.label"
            />
        </div>

        <!-------------------------------------- MORE EXTENDED FIELDS -------------------------------------------------->

        <m-contact-line 
            left_icon="mdi-link"
            label="Website" 
            v-model="contact.website"
            v-show="show_more_fields"
        />

        <m-contact-line 
            left_icon="mdi-cog"
            label="Custom Field" 
            v-model="contact.custom_field"
            v-show="show_more_fields"
        />

        <m-contact-line 
            left_icon="mdi-file"
            label="Notes" 
            v-model="contact.notes"
        />


        </v-form>
       
    </v-card-text>

    <v-card-actions>
         <v-btn
            color="gray darken-1"
            text
            @click="show_more_fields = !show_more_fields"
        >
            <span v-show="!show_more_fields">Show More Fields</span>
            <span v-show="show_more_fields">Show Fewer Fields</span>
        </v-btn>

        <v-btn
            color="gray darken-1"
            text
            v-if="$rc.$state.isCommerce7Enabled()"
            v-on:click="onClickCreateInCommerce7"
        >
            Create customer in Commerce7
        </v-btn>


        <v-spacer></v-spacer>

        <v-btn
            color="gray darken-1"
            text
            @click="dialog = false"
        >
        Cancel
        </v-btn>

        <v-btn
            color="primary darken-1"
            text
            @click="onOk"
            :disabled="!valid"
        >
        Ok
        </v-btn>
    </v-card-actions>
    </v-card>

   
</v-dialog>
</template>

<script>

const E164 = require('@/js/E164.js');
const _ = require('lodash');
const validate = require('@/js/validate.js'); 

const empty_postal_address = {
                "address" : {
                    "address_line1" : null,
                    "address_line2" : null,
                    "address_line3" : null,
                    "city" : null,
                    "state" : null,
                    "postal_code" : null,
                    "country" : "US"
                },
                "label" : null,
            };

const empty_event = {
    date: null,
    label: null,
};

const empty_email_address = {
    email: null,
    label: null,
};

const empty_phone_number = {
    phone_number: null,
    label: null,
};

function isNotBlank(value)
{
    if ( value == null ) return false;
    if ( value.trim() === '' ) return false;
    return true;
}

export default {
    data() {
        return {
            contact: {
                "contact_id" : "00000000-0000-0000-0000-000000000000",
                "organization_id" : "00000000-0000-0000-0000-000000000000",
                "avatar_image" : null,
                "images" : [ ],
                "name" : {
                    "prefix" : null,
                    "first" : null,
                    "middle" : null,
                    "last" : null,
                    "suffix" : null,
                    "phonetic" : null,
                    "nickname" : null,
                    "explicit_label" : null
                },
                "employment_summary" : {
                    "organization_name" : null,
                    "job_title" : null,
                    "department" : null
                },
                "birthday" : null,
                "phone_numbers" : [ ],
                "email_addresses" : [ ],
                "postal_addresses" : [ ],
                "events" : [ ],
                "website" : null,
                "notes" : null,
                "custom_field" : null,
                "time_zone" : null,
                "of_employee_id" : null
                },
            dialog: false,
            valid: false,
            'validate': validate,
            show_more_fields: false,
        }
    },

    methods: {
        

        normalizePhoneNumber(raw_value)
        {
            let normal_value = E164.createEndpointFromUSPhoneNumber(raw_value,null);
            if ( normal_value == null ) return raw_value;
            return normal_value;
        },

        showDialog(contact) 
        {
            this.contact = _.cloneDeep(contact);
            this.show_more_fields = false;

            this.prettyPrintAllPhoneNumbers();
            this.addBlankAddress();
            this.addBlankEvent();
            this.addBlankEmail();
            this.addBlankPhoneNumber();
            this.dialog = true;
            this.checkOptOutStatus();
        },

        addBlankAddress()
        {
            if ( this.contact.postal_addresses.length != 0 )
                return;

            this.contact.postal_addresses.push(_.cloneDeep(empty_postal_address));
        },

        addBlankEmail()
        {
            if ( this.contact.email_addresses.length != 0 )
                return;

            this.contact.email_addresses.push(_.cloneDeep(empty_email_address));
        },

        addBlankPhoneNumber()
        {
            if ( this.contact.phone_numbers.length != 0 )
                return;

            this.contact.phone_numbers.push(_.cloneDeep(empty_phone_number));
        },

        addBlankEvent()
        {
            if ( this.contact.events.length != 0 )
                return;

            this.contact.events.push(_.cloneDeep(empty_event));
        },

        removeBlankAddresses()
        {
            for ( let i = this.contact.postal_addresses.length-1; i >= 0; i-- )
            {
                let address = this.contact.postal_addresses[i].address;

                if ( isNotBlank(address.address_line1) ) continue;
                if ( isNotBlank(address.address_line2) ) continue;
                if ( isNotBlank(address.address_line3) ) continue;
                if ( isNotBlank(address.city) ) continue;
                if ( isNotBlank(address.state) ) continue;
                if ( isNotBlank(address.postal_code) ) continue;
                if ( isNotBlank(address.country) && address.country != 'US' ) continue;

                this.contact.postal_addresses.splice(i,1);
            }
        },

        removeBlankEvents()
        {
            for ( let i = this.contact.events.length-1; i >= 0; i-- )
            {
                let event = this.contact.events[i];

                if ( isNotBlank(event.date) ) continue;
                if ( isNotBlank(event.label) ) continue;

                this.contact.events.splice(i,1);
            }
        },

        removeBlankEmails()
        {
            for ( let i = this.contact.email_addresses.length-1; i >= 0; i-- )
            {
                let email = this.contact.email_addresses[i];

                if ( isNotBlank(email.email) ) continue;
                if ( isNotBlank(email.label) ) continue;

                this.contact.email_addresses.splice(i,1);
            }
        },

        removeBlankPhoneNumbers()
        {
            for ( let i = this.contact.phone_numbers.length-1; i >= 0; i-- )
            {
                let phone_number = this.contact.phone_numbers[i];

                if ( isNotBlank(phone_number.phone_number) ) continue;
                if ( isNotBlank(phone_number.label) ) continue;

                this.contact.phone_numbers.splice(i,1);
            }
        },

        prettyPrintAllPhoneNumbers()
        {
            for ( let i = 0; i < this.contact.phone_numbers.length; i++ )
            {
                this.contact.phone_numbers[i].phone_number = E164.prettyPrintUsPhoneNumber(this.contact.phone_numbers[i].phone_number);
            }
        },

        normalizeAllPhoneNumbers()
        {
            for ( let i = 0; i < this.contact.phone_numbers.length; i++ )
            {
                let raw_value = this.contact.phone_numbers[i].phone_number;
                let normal_value = E164.createEndpointFromUSPhoneNumber(raw_value,null);

                if ( normal_value == null ) 
                    continue;

                normal_value = normal_value.substring("E164:".length);
                this.contact.phone_numbers[i].phone_number = normal_value;
            }
        },
        
        onOk()
        {
           this.dialog = false;
           this.normalizeAllPhoneNumbers();
           this.removeBlankAddresses();
           this.removeBlankEvents();
           this.removeBlankEmails();
           this.removeBlankPhoneNumbers();
           this.$emit('ok', _.cloneDeep(this.contact));
        },

        onClickCreateInCommerce7()
        {
           this.normalizeAllPhoneNumbers();
           this.removeBlankAddresses();
           this.removeBlankEvents();
           this.removeBlankEmails();
           this.removeBlankPhoneNumbers();

           let contact = _.cloneDeep(this.contact);

           this.$rc.rest({
            'spinner': true,
            'endpoint': 'PUT: /api/v1/integrations/crm/create-commerce7-customer-from-contact',
           
            'params' : contact,
           
            'on_success': (data) => {
                    if ( data.success )
                    {
                        if ( data.url != null )
                        {
                            window.open(data.url,"_blank");
                        }

                        this.$rc.snackbar("Customer created in commerce7");
                    }
                    else
                    {
                        this.$rc.snackbar(data.message);
                    }
                },
           
            'on_error': 'Unable to create customer in Commerce7',
           });
        },

        isLastEmail(index)
        {
            return index == this.contact.email_addresses.length - 1;
        },

        deleteEmail(index)
        {
            if ( index == 0 && this.contact.email_addresses.length == 1 )
            {
                this.contact.email_addresses[0].email = null;
                this.contact.email_addresses[0].label = null;
                return;
            }

            this.contact.email_addresses.splice(index, 1);
        },

        addEmail()
        {
            this.contact.email_addresses.push({
                'email' : null,
                'label' : null,
            })
        },

        isLastPhone(index)
        {
            return index == this.contact.phone_numbers.length - 1;
        },

        deletePhone(index)
        {
            if ( index == 0 && this.contact.phone_numbers.length == 1 )
            {
                this.contact.phone_numbers[0].phone_number = null;
                this.contact.phone_numbers[0].label = null;
                return;
            }

            this.contact.phone_numbers.splice(index, 1);
        },

        isPhoneDisabled(index,phone)
        {
            if ( index == 0 && this.contact.of_employee_id != null )
                return true;

          if (phone.contact_phone_number_source != null && phone.contact_phone_number_source != 'manual-from-contacts')
          {
            return true;
          }

          return false;
        },

        addPhone()
        {
            this.contact.phone_numbers.push({
                'phone_number' : null,
                'label' : null,
            })
        },

        isLastAddress(index)
        {
            return index == this.contact.postal_addresses.length - 1;
        },

        deleteAddress(index)
        {
            if ( index == 0 && this.contact.postal_addresses.length == 1 )
            {
                this.contact.postal_addresses[0] = _.cloneDeep(empty_postal_address);
                this.$forceUpdate();
                return;
            }

            this.contact.postal_addresses.splice(index, 1);
        },

        addAddress()
        {
            this.contact.postal_addresses.push(_.cloneDeep(empty_postal_address))
        },

        isLastEvent(index)
        {
            return index == this.contact.events.length - 1;
        },

        deleteEvent(index)
        {
            if ( index == 0 && this.contact.events.length == 1 )
            {
                this.contact.events[0] = _.cloneDeep(empty_event);
                this.$forceUpdate();
                return;
            }

            this.contact.events.splice(index, 1);
        },

        addEvent()
        {
            this.contact.events.push(_.cloneDeep(empty_event))
        },

    },
    computed: {
    },
}

</script>

<style lang="scss" scoped>
.left_icon_column
{
    min-width: 40px;
}
.right_icon_column
{
    min-width: 60px;
}
</style>